import React, { useState } from "react";
import { Building2, MapPin, Globe, Link as LinkIcon, BookOpen, Users, Trophy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";
import { ImageCheck } from "Explore/Components";
import { ImageCheckType } from "Explore/Config/ImageType";

const formatCompact = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "N/A";
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(num);
};

export const InstitutionHeader = ({ institution, kpis, isLoading }) => {
  const stats = [
    { label: "Publications", value: kpis?.publications ?? institution.publication_count ?? institution.total_publication, icon: BookOpen },
    { label: "Citations", value: kpis?.citations ?? institution.citation_count ?? institution.total_citation, icon: LinkIcon },
    { label: "Researchers", value: kpis?.researchers ?? institution.researcher_count ?? institution.total_faculties, icon: Users },
    { label: "h-index", value: kpis?.h_index ?? institution.h_index ?? institution.total_h_index, icon: Trophy }
  ];

  return (
    <Card className="overflow-hidden border-0 shadow-sm ring-1 ring-border/50 relative flex flex-col">
      <div className="h-[180px] sm:h-[200px] w-full relative bg-muted group">
        <ImageCheck
          url={`https://media.academic.online/institutions_images/${institution.id}/landscape.png`}
          type={ImageCheckType.INSTITUTION_LANDSCAPE}
          className="absolute inset-0 w-full h-full object-cover"
          altImg={`${institution.name} background`}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
        
        {/* Content over banner */}
        <div className="absolute bottom-0 left-0 w-full px-6 py-4 flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row gap-6 items-end">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white border-4 border-white/20 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-2xl relative z-10 translate-y-8 sm:translate-y-10">
              <ImageCheck
                url={`https://media.academic.online/institutions_images/${institution.id}/seal.png`}
                type={ImageCheckType.INSTITUTION_LOGO}
                className="w-full h-full object-contain bg-white p-2"
                altImg={`${institution.name} logo`}
              />
            </div>
            
            <div className="flex flex-col gap-1.5 flex-1 min-w-0 pb-1">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white m-0 truncate drop-shadow-md">
                {institution.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-white/90 drop-shadow">
                {(institution.city || institution.state || institution.country) && (
                  <div className="flex items-center gap-1.5">
                    <MapPin size={14} />
                    <span>
                      {[institution.city, institution.state, institution.country].filter(Boolean).join(", ")}
                    </span>
                  </div>
                )}
                {(institution.official_web || institution.domain_web) && (
                   <div className="flex items-center gap-1.5">
                      <Globe size={14} />
                      <a 
                        href={(institution.official_web || institution.domain_web).startsWith('http') ? (institution.official_web || institution.domain_web) : `https://${(institution.official_web || institution.domain_web)}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-white hover:underline transition-colors"
                      >
                        Website
                      </a>
                    </div>
                )}
                {(institution.wikipedia_url || institution.wikipedia) && (
                   <div className="flex items-center gap-1.5">
                      <BookOpen size={14} />
                      <a 
                        href={(institution.wikipedia_url || institution.wikipedia).startsWith('http') ? (institution.wikipedia_url || institution.wikipedia) : `https://${(institution.wikipedia_url || institution.wikipedia)}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-white hover:underline transition-colors"
                      >
                        Wikipedia
                      </a>
                    </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar Integrated (Moved out of banner) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 pt-10 sm:pt-6 bg-card border-t pl-[140px] sm:pl-[170px]">
        {stats.map((stat, idx) => {
          const hasData = stat.value !== undefined && stat.value !== null && !isNaN(stat.value);
          const displayValue = hasData ? formatCompact(stat.value) : "No data available";
          
          return (
            <div key={idx} className="flex flex-col gap-1 border-r last:border-0 px-2">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <stat.icon size={14} className="stroke-[2]" />
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider">{stat.label}</span>
              </div>
              {isLoading ? (
                <Skeleton className="h-6 sm:h-8 w-20 sm:w-24 mt-1" />
              ) : (
                <span className={`truncate ${!hasData ? "text-sm text-muted-foreground italic mt-1" : "text-xl sm:text-2xl font-bold text-foreground mt-0.5"}`}>
                  {displayValue}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export const BiographyCard = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>About</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-[15px] text-foreground/80 leading-relaxed whitespace-pre-line text-justify ${expanded ? "" : "line-clamp-6"}`}>
          {text}
        </div>
        {text.length > 300 && (
          <Button variant="link" className="px-0 mt-2" onClick={() => setExpanded(!expanded)}>
            {expanded ? "Show less" : "Show more"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
